import { ref, reactive, nextTick } from 'vue'

export function useTabController(
  initialTabId: string,
  allTabs: Array<{ id: string, name: string, disabled: boolean, hide: boolean }>,
  // When set, the current step id is written to sessionStorage under this key on every change,
  // so a browser reload can resume the journey where the user left off (see Factory's resume logic).
  persistKey?: string,
) {
  const currentTabId = ref<string>(initialTabId)
  const allItems = reactive(allTabs)

  // Get the current order of tabs based on allItems
  const order = allItems.map(i => i.id) as string[]

  const persist = (id: string) => {
    if (!persistKey) return
    try { sessionStorage.setItem(persistKey, id) } catch { /* storage unavailable (private mode) */ }
  }

  const show = (id: string) => {
    allItems.forEach(item => {
      if (item.id === id) {
        item.disabled = false;
      }
    });
    currentTabId.value = id
    persist(id)
    scrollToTop()
  }

  // Scroll the window back to the top after a tab change.
  const scrollToTop = () => {
    nextTick(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            window.scrollTo({ top: 0, behavior: 'smooth' })
          } catch {
            window.scrollTo(0, 0)
          }
        })
      })
    })
  }

  const goNext = (fromId: string = currentTabId.value) => {
    const i = order.indexOf(fromId)
    if (i === -1 || i >= order.length - 1) return
    show(order[i + 1])
  }

  const goBack = (fromId: string = currentTabId.value) => {
    const i = order.indexOf(fromId)
    if (i <= 0) {
      show(order[0])
      return
    }
    show(order[i - 1])
  }

  const goLast = () => {
    const allItemsWithoutHidden = allItems.filter(i => !i.hide);
    allItemsWithoutHidden.forEach(item => item.disabled = false);
    const lastItem = allItemsWithoutHidden[allItemsWithoutHidden.length - 1];
    if (lastItem) {
      show(lastItem.id);
    }
  }

  return { currentTabId, show, goNext, goBack, goLast, allItems }
}
